import styled from "@emotion/styled";
import {
	AccountCircleOutlined,
	NotificationsOutlined,
} from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import {
	AppBar,
	Box,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Toolbar,
} from "@mui/material";
import { useState } from "react";

import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useSignOut } from "Auth/Hooks/useSignOut";
import {
	GlobalResources,
	useResource,
} from "Infrastructure/Translations/Resources";

const StyledAppBar = styled(AppBar)`
	background-color: #FFFFFF;
	box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
	border-bottom: 1px solid #E9EAEB;
`;

const Logo = styled("img")`
	height: 40px;
	width: auto;
`;

type HeaderProps = {
	title?: string;
};

export const Header = ({ title }: HeaderProps) => {
	const { t } = useResource();
	const { data } = useUserInfoQuery();
	const signOut = useSignOut();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const userLogin = data?.status === 200 ? data.data.login : "";

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSignOut = () => {
		handleClose();
		signOut();
	};

	return (
		<StyledAppBar position="static" elevation={0} sx={{ paddingY: 0 }}>
			<Toolbar
				sx={{
					minHeight: 64,
					maxWidth: "1140px",
					margin: "0 auto",
					width: "100%",
				}}
			>
				<Box
					sx={{ display: "flex", alignItems: "center", flexGrow: 1, gap: 3 }}
				>
					<Logo src="/SLS_Logo.svg" alt="Swiss Life Select" />
					{title && (
						<Box
							component="h1"
							sx={{
								margin: 0,
								fontSize: "20px",
								fontWeight: 600,
								color: "primary.main",
							}}
						>
							{title}
						</Box>
					)}
				</Box>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton onClick={() => {}} size="small">
						<NotificationsOutlined sx={{ fontSize: 24 }} />
					</IconButton>
					<IconButton
						onClick={handleClick}
						size="small"
						aria-controls={open ? "account-menu" : undefined}
						aria-haspopup="true"
						aria-expanded={open ? "true" : undefined}
					>
						<AccountCircleOutlined sx={{ fontSize: 24 }} />
					</IconButton>
				</Box>

				<Menu
					anchorEl={anchorEl}
					id="account-menu"
					open={open}
					onClose={handleClose}
					onClick={handleClose}
					transformOrigin={{ horizontal: "right", vertical: "top" }}
					anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
					slotProps={{
						paper: {
							elevation: 0,
							sx: {
								overflow: "visible",
								filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.12))",
								mt: 1.5,
								minWidth: 200,
								"&::before": {
									content: '""',
									display: "block",
									position: "absolute",
									top: 0,
									right: 14,
									width: 10,
									height: 10,
									bgcolor: "background.paper",
									transform: "translateY(-50%) rotate(45deg)",
									zIndex: 0,
								},
							},
						},
					}}
				>
					<MenuItem>
						<ListItemText
							primary={userLogin}
							secondary="Přihlášený uživatel"
							slotProps={{
								primary: {
									variant: "body2",
								},
								secondary: {
									variant: "caption",
								},
							}}
						/>
					</MenuItem>
					<Divider />
					<MenuItem onClick={handleSignOut}>
						<ListItemIcon>
							<LogoutIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t(GlobalResources.Common.SignOut)}</ListItemText>
					</MenuItem>
				</Menu>
			</Toolbar>
		</StyledAppBar>
	);
};
